import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { authApi } from '@/api/auth'
import type { User } from '@/lib/pos-types'

type AuthState =
  | { status: 'loading'; user: User | null }
  | { status: 'ready'; user: User | null }

type AuthContextValue = {
  status: AuthState['status']
  user: User | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null })

  const refresh = useCallback(async () => {
    const token = authApi.getToken()
    if (!token) {
      setState({ status: 'ready', user: null })
      return
    }
    try {
      const user = await authApi.me()
      setState({ status: 'ready', user })
    } catch {
      authApi.clearToken()
      setState({ status: 'ready', user: null })
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => {
      void refresh()
    }
    window.addEventListener('pos-auth-changed', handler)
    return () => window.removeEventListener('pos-auth-changed', handler)
  }, [refresh])

  const login = useCallback(async (email: string, password: string) => {
    setState(prev => ({ status: 'loading', user: prev.user }))
    try {
      const user = await authApi.login(email, password)
      setState({ status: 'ready', user })
    } catch (e) {
      setState(prev => ({ status: 'ready', user: prev.user }))
      throw e
    }
  }, [])

  const logout = useCallback(async () => {
    setState(prev => ({ status: 'loading', user: prev.user }))
    await authApi.logout()
    setState({ status: 'ready', user: null })
  }, [])

  const value = useMemo<AuthContextValue>(() => {
    const user = state.user
    return {
      status: state.status,
      user,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refresh,
    }
  }, [login, logout, refresh, state.status, state.user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider />')
  return ctx
}
